/**
 * Product Context Service (Frontend)
 * 
 * This module provides utilities for building product context
 * to be sent to the AI for image generation.
 * 
 * NOTE: This mirrors the backend version in functions/src/productContextBuilder.ts
 * Any changes should be synchronized between both files.
 */

import { ShopifyProduct } from './api';

// Options for context building
export interface ProductContextOptions {
    includeImages?: boolean;        // Whether to include image URLs in context
    maxDescriptionLength?: number;  // Max chars for description (default: 300)
    maxImages?: number;             // Max number of images to include (default: 3)
}

/**
 * Build the product context string from product data.
 * This is the SINGLE SOURCE OF TRUTH for context formatting (frontend version).
 */
export function buildProductContextText(
    product: ShopifyProduct,
    options: ProductContextOptions = {}
): string {
    const {
        includeImages = true,
        maxDescriptionLength = 300,
        maxImages = 3,
    } = options;

    const contextParts: string[] = [];

    contextParts.push("[CONTEXTE PRODUIT]");
    contextParts.push(`Nom du produit: ${product.title}`);

    if (product.vendor) {
        contextParts.push(`Marque: ${product.vendor}`);
    }

    // Get description (strip HTML if present)
    if (product.body_html) {
        const description = product.body_html
            .replace(/<[^>]*>/g, " ")
            .replace(/\s+/g, " ")
            .trim()
            .substring(0, maxDescriptionLength);
        if (description) {
            contextParts.push(`Description: ${description}`);
        }
    }

    // Tags
    if (product.tags && (Array.isArray(product.tags) ? product.tags.length > 0 : product.tags.length > 0)) {
        const tagsString = typeof product.tags === "string"
            ? product.tags
            : product.tags.join(", ");
        contextParts.push(`Tags: ${tagsString}`);
    }

    // Product type
    if (product.product_type) {
        contextParts.push(`Type: ${product.product_type}`);
    }

    // Add existing image URLs for reference
    if (includeImages && product.images && product.images.length > 0) {
        const imageUrls = product.images
            .slice(0, maxImages)
            .map((img) => img.src)
            .join(", ");
        contextParts.push(`Images existantes: ${imageUrls}`);
    }

    contextParts.push("[FIN CONTEXTE]");
    contextParts.push("");
    contextParts.push("[INSTRUCTION DE GÉNÉRATION]");

    return contextParts.join("\n");
}

/**
 * Build the full prompt with product context and optional negative prompt.
 */
export function buildFullPrompt(
    basePrompt: string,
    productContext: string | null,
    negativePrompt?: string
): string {
    let fullPrompt = "";

    if (productContext) {
        fullPrompt = productContext + basePrompt;
    } else {
        fullPrompt = basePrompt;
    }

    if (negativePrompt && negativePrompt.trim()) {
        fullPrompt += `\n\n---\n[NE PAS INCLURE] ${negativePrompt.trim()}`;
    }

    return fullPrompt;
}
